import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { hasRole, Role } from '@nexa/auth';
import { useSession } from '@/auth/use-session';
import { DelayDialog } from '@/components/delay/header-delay';
import type { IDelayDialogPreset } from '@/components/delay/disruption-delay-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { flightOptionsOf } from '@/lib/delay/format';
import { useDisruptionDelays } from '@/lib/delay/use-delay';
import type { DisruptionDetail } from '@/lib/disruptions/types';
import { OUT_OF_POLICY_BUTTON, OutOfPolicyLine } from './out-of-policy-line';

// The disruption header's out-of-policy alert (delay-limits U2). A provider (push / pull)
// reported a delay over the policy maximum: cases RECORDED it and applied nothing — no
// ETD move, no care, no advisory (brief §6 invariant 6). The operator decides:
//
//   «Declarar cancelado» — the delay dialog opens on that flight, «Actualizar ETD» already
//     on CANCELLED (the declaration then escalates, B5);
//   «Descartar» — the report stays in the history, the flag clears (idempotent at cases).
//
// Both are delay writes: hidden below AIRPORT_OPERATOR, like the BFF's gate.
//
// Data: the disruption's delay timelines (the SAME query and cache entry as the header
// badge and the escalation alert — one read), kept live by the WS merge
// (`FlightDelayChanged` carries `outOfPolicy`; a dismiss / in-policy report / escalation /
// departure clears it) — no poll. Before they answer, the row's `flightStatus.outOfPolicy`
// says one exists.
//
// UX states: EMPTY — nothing flagged ⇒ nothing rendered; LOADING — the row is flagged but
// only the timelines can say more ⇒ a skeleton line; ERROR — that read failed ⇒ one line +
// Retry; SUCCESS — one line per flagged flight (`OutOfPolicyLine`, shared with the delay
// panel), «Descartar» with its pending / error state.
// Phone: everything wraps inside the header, the buttons are 44 px targets below `sm`.
// Theming: tokens only.

export function OutOfPolicyAlert({ detail }: { detail: DisruptionDetail }) {
  const { t } = useTranslation(['disruptions', 'common']);
  const { role } = useSession();
  const [preset, setPreset] = useState<IDelayDialogPreset | null>(null);
  const status = detail.flightStatus;
  const delays = useDisruptionDelays(
    detail.urn,
    status?.outOfPolicy === true || typeof status?.maxDelayMinutes === 'number',
  );
  const flagged = (delays.data ?? []).filter((d) => d.outOfPolicy);

  if (flagged.length === 0) {
    // The timelines, once read, are fresher than the row.
    if (status?.outOfPolicy !== true || delays.data) return null;
    if (delays.isError) {
      return (
        <div
          className="mt-2 flex max-w-full flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs"
          role="alert"
          data-testid="out-of-policy-alert-error"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
          <span className="min-w-0 break-words">{t('delay.panel.error.title')}</span>
          <Button type="button" size="sm" variant="outline" className={OUT_OF_POLICY_BUTTON} onClick={() => void delays.refetch()}>
            {t('delay.panel.error.retry')}
          </Button>
        </div>
      );
    }
    return (
      <div className="mt-2 max-w-full" data-testid="out-of-policy-alert-loading" aria-busy="true">
        <span className="sr-only">{t('common:loading')}</span>
        <Skeleton className="h-7 w-full max-w-md" />
      </div>
    );
  }

  const labels = flagged.length > 1 ? new Map(flightOptionsOf(detail.cases ?? [], flagged).map((o) => [o.caseUrn, o.label])) : null;
  return (
    <>
      {flagged.map((d) => (
        <OutOfPolicyLine
          key={d.caseUrn}
          className="mt-2"
          delay={d}
          report={d.outOfPolicy!}
          disruptionUrn={detail.urn}
          label={labels?.get(d.caseUrn) ?? null}
          canAct={hasRole(role, Role.AIRPORT_OPERATOR)}
          onCancel={() => setPreset({ caseUrn: d.caseUrn, kind: 'CANCELLED' })}
        />
      ))}
      <DelayDialog
        detail={detail}
        open={preset !== null}
        onOpenChange={(open) => {
          if (!open) setPreset(null);
        }}
        preset={preset ?? undefined}
      />
    </>
  );
}
